import * as React from "react";
import { ColumnAnalysisView } from "./types";
import { GeneratePrompt } from "./generatePrompt";
import { AnalysisResult } from "./analysisResult";
import { Conversation } from "./conversation";
import { SavedAnalyses } from "./savedAnalyses";
import { ColumnAnalysisResponse } from "@/modules/snapshots/businessReports.types";
import { Button } from "@/components/ui/button";
import { BarChart3, MessageSquare, Bookmark } from "lucide-react";

interface Props {
  businessReportId: string;
  snapshotCollectionId: string;
  subcollectionId?: string;
  fieldName: string;
  contractFamilyProjection?: boolean;
}

export const GridSidebarColumnAnalysis: React.FC<Props> = ({
  businessReportId,
  snapshotCollectionId,
  subcollectionId,
  fieldName,
  contractFamilyProjection = false,
}) => {
  const [view, setView] = React.useState<ColumnAnalysisView>("generate");
  const [currentPrompt, setCurrentPrompt] = React.useState<string>("");
  const [currentResult, setCurrentResult] = React.useState<string | null>(null);

  const handlePromptGenerated = (prompt: string) => {
    setCurrentPrompt(prompt);
    setCurrentResult(null);
    setView("result");
  };

  const handleAnalysisSelected = (analysis: ColumnAnalysisResponse) => {
    setCurrentPrompt(analysis.prompt);
    setCurrentResult(analysis.result ?? null);
    setView("result");
  };

  const handleBackToGenerate = () => {
    setView("generate");
    setCurrentPrompt("");
    setCurrentResult(null);
  };

  const handleSaved = () => {
    setView("saved");
  };

  const tabs = [
    { id: "generate" as const, label: "Generate Analysis", icon: BarChart3 },
    { id: "conversation" as const, label: "Chat", icon: MessageSquare },
    { id: "saved" as const, label: "Saved", icon: Bookmark },
  ];

  const isTabActive = (tabId: ColumnAnalysisView) => view === tabId || (view === "result" && tabId === "generate");

  return (
    <div className="flex h-full flex-col">
      <div className="flex gap-2 border-b p-2 pl-4">
        {tabs.map(tab => (
          <Button
            key={tab.id}
            variant={isTabActive(tab.id) ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setView(tab.id)}
          >
            <tab.icon className="mr-2 h-4 w-4" />
            {tab.label}
          </Button>
        ))}
      </div>

      <div className="flex-1 overflow-hidden">
        {view === "generate" && (
          <GeneratePrompt
            businessReportId={businessReportId}
            snapshotCollectionId={snapshotCollectionId}
            subcollectionId={subcollectionId}
            fieldName={fieldName}
            onPromptGenerated={handlePromptGenerated}
            contractFamilyProjection={contractFamilyProjection}
          />
        )}

        {view === "result" && (
          <AnalysisResult
            businessReportId={businessReportId}
            snapshotCollectionId={snapshotCollectionId}
            subcollectionId={subcollectionId}
            fieldName={fieldName}
            prompt={currentPrompt}
            initialResult={currentResult}
            onBack={handleBackToGenerate}
            onSaved={handleSaved}
            contractFamilyProjection={contractFamilyProjection}
          />
        )}

        {view === "conversation" && (
          <Conversation
            businessReportId={businessReportId}
            snapshotCollectionId={snapshotCollectionId}
            subcollectionId={subcollectionId}
            fieldName={fieldName}
            contractFamilyProjection={contractFamilyProjection}
          />
        )}

        {view === "saved" && (
          <SavedAnalyses
            snapshotCollectionId={snapshotCollectionId}
            subcollectionId={subcollectionId}
            fieldName={fieldName}
            onSelect={handleAnalysisSelected}
            contractFamilyProjection={contractFamilyProjection}
          />
        )}
      </div>
    </div>
  );
};
